import React, {useMemo, useState, useEffect} from 'react';
import styled from 'styled-components';
import useForm from 'react-hook-form';

import Breadcrumbs from '../../../components/NavBar/components/Breadcrumbs';
import Overview, {SubTitle} from '../../components/Cards/Overview';

import {getTestProgress, getTest, getTestProgressIdsByUserId, getTestProgressIdsByIdOverride} from '@bootcamp/shared/src/requests';

import {useUserDataContext} from '../../../contexts/UserData';
import {useBootcampConfigContext} from '../../../contexts/BootcampConfig';
import {getInObj} from '@bootcamp/shared/src/util';
import moment from 'moment';
import {Helmet} from 'react-helmet';

import {findInConfigById, createPathFromConfigMatch} from '@bootcamp/shared/src/util';
import {getResults, getOATResults, aggregateBySection, aggregateByOATSection, getAcademicAverage, getOATAcademicAverage} from '@bootcamp/shared/src/util/scoring';
import {restoreQuizProgress} from '../Performance';

import {ChevronRight} from '@styled-icons/fluentui-system-filled/ChevronRight';
import {theme} from '@bootcamp/shared/src/styles/theme';

import Container, {resetScrollPosition} from '../../pages/PageContainer';
import {firstBy} from 'thenby';

import {H1, Label3} from '../../../components/Typography/next';

import {ScoreCell, CellWrapper, AvgScoreCell} from '@bootcamp/web/src/bootcamps/pages/PreviousTests';
import Table from '@bootcamp/web/src/bootcamps/components/PreviousTestsTable';


const retrieveQuestionProgresses = testData => {
  const {blockProgresses: {items: blockProgressItems}} = testData || {blockProgresses: {items: []}};
  return blockProgressItems.reduce((acc, blockProgress) => [...acc, ...(blockProgress.questionProgresses.items || [])], []);
}

async function getTestProgresses(userId) {
  const data = await getTestProgressIdsByUserId(userId);
  return getInObj(['data', 'getUser', 'testProgresses', 'items'], data, []);
}

async function getDBCTestProgresses(userId) {
  const data = await getTestProgressIdsByIdOverride(userId);
  return getInObj(['data', 'getWordPressUserData', 'testProgresses', 'items'], data, []);
}

function filterAndSortProgresses(items, bootcamp, config) {
  try {
    switch (bootcamp) {
      case 'dat':
      case 'oat':
        return items
          ?.sort(firstBy('createdAt', -1))
          ?.reduce((acc, item) => {
            if (item.testId === '19c272d3-8413-4136-afbe-35a54242d725') return [...acc, {...item, route: '/sample-test'}];
            const configHit = findInConfigById(config, item.testId);
            const fltConfigHit = config?.studyTools?.[0]?.content?.find(({id}) => id === item.testId);

            const match = fltConfigHit ? [fltConfigHit] : configHit;
            const isValid = match?.[0]?.name?.match('Test #');
            const route = fltConfigHit
              ? `/full-length/${fltConfigHit?.route}`
              : createPathFromConfigMatch(match);

            return isValid
              ? [...acc, {...item, route}]
              : acc;
          }, []);
      default:
        return items;
    }

  } catch (error) {
    return items;
  }
}

const StyledBreadcrumbs = styled(Breadcrumbs)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.s};
`;

const StyledOverview = styled(Overview)`
  margin-bottom: ${({theme}) => theme.layouts.spacing.m};
  background: none;
  border: none;
  box-shadow: none;
  padding: 0px;

  ${SubTitle} {
    margin-bottom: 0;
  }

  ${({theme}) => theme.mediaQueries.tablet} {
    margin-bottom: 0px;
  }
`;

const SubjectPill = styled(Label3)`
  display: flex;
  background: ${({palette}) => palette.light};
  color: ${({palette}) => palette.default};
  padding: 4px  8px;
  border-radius: 8px;
  white-space: nowrap;
`;
const DateTaken = styled.div`
  white-space: nowrap;
`;
const StyledTable = styled(Table)`
  table {
    td {
      padding: 16px !important;

      &:first-child {
        padding-left: 24px !important;
      }
    }
  }
`;
const Title = styled(H1)`
  color: white;
  margin-bottom: 16px;
`;

const PreviousTests = ({history, match}) => {
  const {DEFAULT_USER_ID, userModel, bootcamp} = useUserDataContext();

  const {config} = useBootcampConfigContext();

  const [currentPage, setCurrentPage] = useState({pageIndex: 0, pageSize: 10});
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  const [testsById, setTestsById] = useState({});
  const [progressById, setProgressById] = useState({});

  const [baseProgress, setBaseProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  const pageCount =   Math.ceil(baseProgress?.length / currentPage.pageSize);

  const {register, watch} = useForm();

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  async function fetchTests(results) {
    // fetch tests
    const defaultTestIds = bootcamp === 'dat' ? ['19c272d3-8413-4136-afbe-35a54242d725'] : []
    const testIdsToFetch = results.reduce((acc, {testId}) => (acc.includes(testId) || testsById[testId]) ? acc : [...acc, testId], defaultTestIds);
    const testPromises = testIdsToFetch.map(testId => getTest(testId, 'getTestConfig'));

    // fetch progress
    const progressIdsToFetch = results.reduce((acc, {id}) => (acc.includes(id) || progressById[id]) ? acc : [...acc, id], []);
    const progressPromises = progressIdsToFetch.map(id => getTestProgress(id, 'getTestProgress'));

    // short circuit if nothing to fetch
    if (!testIdsToFetch.length && !progressIdsToFetch.length) {
      setLoading(false);
      return;
    };

    setLoading(true);

    const testPromiseResults = await Promise.all(testPromises);
    const progressPromiseResults = await Promise.all(progressPromises);

    const fetchedProgress = progressPromiseResults.reduce((acc, curr) => {
      const progress = curr?.data?.getTestProgress;

      return progress?.id
        ? {...acc, [progress?.id]: progress}
        : acc
    }, {});

    const fetchedTests = testPromiseResults.reduce((acc, curr) => {
      const test = curr?.data?.getTest;

      return test?.id
        ? {...acc, [test?.id]: test}
        : acc
    }, {});

    setTestsById({...testsById, ...fetchedTests});
    setProgressById({...progressById, ...fetchedProgress});
    setLoading(false);
  }

  async function handleNextPage() {
    if (loading) return;
    // when we're moving to the next page, just fetch the next page of test
    const nextPageIndex = currentPage.pageIndex + 1;
    const nextPage = baseProgress?.slice(nextPageIndex * currentPage.pageSize, (nextPageIndex + 1) * currentPage.pageSize);

    setCurrentPage({pageIndex: nextPageIndex, pageSize: currentPage.pageSize})
    fetchTests(nextPage);
  }

  async function handlePreviousPage() {
    if (loading) return;
    setCurrentPage({pageIndex: currentPage.pageIndex - 1, pageSize: currentPage.pageSize});
  }

  async function handlePageSize(newPageSize) {
    const nextPage = baseProgress?.slice(0 * newPageSize, (1) * newPageSize);

    await fetchTests(nextPage);

    setCurrentPage({pageIndex: 0, pageSize: newPageSize});
  }

  function getScore(questionProgresses) {
    return (questionProgresses.filter(({didSelectCorrectAnswer}) => didSelectCorrectAnswer).length)
  }

  function getSubjectFromTestTitle(testTitle) {
    if (testTitle === 'DAT Sample Test') return 'Multiple';
    const subjectString = testTitle
      ?.replace('DAT Bootcamp | ', '')
      ?.split('Test #')[0];
    const subject = subjectString?.match('Full Length') ? 'Multiple' : subjectString;

    return subject?.trim();
  }

  const columns = useMemo(
    () => [
      {
        Header: '#',
        Cell: ({row}) => {
          return baseProgress.length - row.index - (currentPage.pageIndex*currentPage.pageSize);
        }
      },
      {
        Header: 'Raw Score',
        Cell: ({row}) => {
          const questionProgresses = retrieveQuestionProgresses(row.original);
          const rawScore = Math.round(getScore(questionProgresses));
          const avgScore = '65%';

          return <ScoreCell score={rawScore} avgScore={avgScore} maxScore={questionProgresses.length} hideBar/>
        },
      },
      {
        Header: 'Test Name',
        Cell: ({row}) => {
          const {testTitle} = row.original;
          return testTitle;
        }
      },
      {
        Header: 'Subject',
        Cell: ({row}) => {
          const {testTitle} = row.original;
          const subject = getSubjectFromTestTitle(testTitle);
          const classroom = config?.classrooms?.find(({name}) => name === subject);

          const palette = subject === 'Multiple'
            ? { default: theme.colors.neutralsPalette.grey, light: theme.colors.neutralsPalette.extraLight}
            : {default: classroom?.themePalette?.default, light: theme?.colors?.assignmentColorPalette[classroom?.themePaletteString || 'grey']?.light};

          return (
            <CellWrapper>
              <SubjectPill palette={palette}>{subject}</SubjectPill>
            </CellWrapper>
          );

        },
      },
      {
        Header: 'Estimated Score',
        Cell: ({row}) => {
          const {testTitle} = row.original;
          const questionProgresses = retrieveQuestionProgresses(row.original);
          const score = getScore(questionProgresses) / questionProgresses.length * 100;
          const subject = getSubjectFromTestTitle(testTitle);
          if (testTitle === 'DAT Sample Test') return `N/A`;
          if (subject === 'Multiple') {
            const quizProgress = restoreQuizProgress(row.original);
            const subjectScores = bootcamp === 'dat'
              ? aggregateBySection(quizProgress)
              : aggregateByOATSection(quizProgress);

            const fullLengthAcademicAverage = bootcamp === 'dat'
              ? getAcademicAverage(testTitle, subjectScores.statistics)
              : getOATAcademicAverage(testTitle, subjectScores.statistics);

            return fullLengthAcademicAverage;
          }

          const academicAverage = bootcamp === 'dat'
            ? getResults(score, testTitle, subject)?.score
            : getOATResults(score, testTitle, subject);
          return academicAverage;
        },
        meta: {
          style: {
            whiteSpace: 'nowrap',
          }
        }
      },
      {
        Header: 'Date Taken',
        Cell: ({row}) => {
          const {createdAt} = row.original;
          return <DateTaken>{moment(createdAt).format('ll')}</DateTaken>
        },
        accessor: row => new Date(row.createdAt),
        sortType: 'datetime'
      },
      {
        Header: ' ',
        Cell: () => <ChevronRight size={16} color="#C8C9CE" />
      }
    ].filter(column => containerWidth < 768 ? !['Subject', 'Date Taken'].includes(column.Header) : true),
    [baseProgress, currentPage, containerWidth]
  );

  const baseData = baseProgress
    ?.slice(currentPage.pageIndex * currentPage.pageSize, (currentPage.pageIndex + 1) * currentPage.pageSize)
    ?.reduce((acc, data, index) => {

      const {id, testId, route} = data;

      const progress = progressById[id];
      const test = testsById[testId];

      if (!progress || !data) return acc;

      const { config: { title: testTitle } } = test || { config: { title: '' } };

      // filters
      const { date, subject, score, search } = watch();

      const testDateOutsideFilter = moment(progress?.createdAt) < moment().subtract(date, 'days');
      if (date && testDateOutsideFilter) return acc;

      if (subject && !testTitle.match(subject)) return acc;

      try {

        const questionProgresses = retrieveQuestionProgresses(progress);
        const testScore = (questionProgresses.filter(({ didSelectCorrectAnswer }) => didSelectCorrectAnswer).length / questionProgresses.length).toFixed(3) * 100;
        if (score && testScore < score) return acc;

        if (search && !JSON.stringify(progress).match(search)) return acc;

        return [
          ...acc,
          {
            ...test,
            ...progress,
            route,
            testTitle
          }
        ]
      } catch (error) {
        return acc;
      }
    }, []);

  const hasResults = baseData?.length > 0;

  const placeholderData = Array.from({ length: currentPage.pageSize }, (_, index) => ({
    id: `placeholder-${index}`,
    testId: `placeholder-test-${index}`,
    route: '/placeholder-route',
    testTitle: `${config?.classrooms?.map(classroom => classroom?.name)[index % 5]} Test #${index + 1}`,
    createdAt: new Date().toISOString(),
    blockProgresses: {items: []},
    questionProgresses: Array.from({ length: 10 }, () => ({
      didSelectCorrectAnswer: Math.random() > 0.5,
    })),
  }));


  const data = hasResults ? baseData : placeholderData;

  useEffect(() => {
    if (!DEFAULT_USER_ID) return;

    async function fetch() {
      const results = userModel.idOverride ? await getDBCTestProgresses(userModel.idOverride) : await getTestProgresses(DEFAULT_USER_ID);

      const filteredItems = filterAndSortProgresses(results, bootcamp, config);

      setBaseProgress(filteredItems);

      const firstPage = filteredItems?.slice(0, 10);

      fetchTests(firstPage);
    }

    fetch();

  }, [userModel, DEFAULT_USER_ID]);


  return (
    <Container>
      <Helmet>
        <title>{config.meta.siteTitle} | Previous Tests</title>
        <meta name="description" content={`${config.meta.siteTitle} Previous Tests`}></meta>
      </Helmet>
      <StyledBreadcrumbs history={history} lockAtDepth={2} customActiveTitle={'Previous Tests'}/>
      <Title>Previous Tests</Title>
      <StyledTable
        columns={columns}
        data={data}
        hasResults={hasResults}
        loading={loading}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageSize={handlePageSize}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        />
    </Container>
  )
};

export default PreviousTests;
